import { Component, computed, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastItemComponent } from './toast-item.component';
import { ToastStore } from './toast.store';
import type { ToastId, ToastPosition, ToastRecord } from './toast.model';

@Component({
  selector: 'app-toast-host',
  standalone: true,
  imports: [CommonModule, ToastItemComponent],
  template: `
    <div
      style="position: fixed; inset: 0; z-index: 2147483647; pointer-events: none;"
      aria-live="polite"
    >
      <div [class]="containerClass()">
        @for (toast of visibleToasts(); track toast.id) {
          <app-toast-item
            [toast]="toast"
            (dismiss)="dismiss($event)"
            (cancelClick)="onCancel(toast, $event)"
          />
        }
      </div>
    </div>
  `,
})
export class ToastHostComponent {
  private readonly _store = inject(ToastStore);

  /**
   * Host-level default position. Individual toasts can override.
   */
  @Input() defaultPosition: ToastPosition = 'bottom-right';

  protected readonly visibleToasts = this._store.visibleToasts;

  protected readonly containerClass = computed(() => {
    // Use the position of the newest toast if it overrides, otherwise default.
    const first = this.visibleToasts()[0];
    const pos = first?.position ?? this.defaultPosition;

    const base = 'pointer-events-none flex flex-col gap-2 p-4';
    switch (pos) {
      case 'top-left':
        return `${base} items-start justify-start absolute top-0 left-0`;
      case 'top-center':
        return `${base} items-center justify-start absolute top-0 left-1/2 -translate-x-1/2`;
      case 'top-right':
        return `${base} items-end justify-start absolute top-0 right-0`;
      case 'bottom-left':
        return `${base} items-start justify-end absolute bottom-0 left-0`;
      case 'bottom-center':
        return `${base} items-center justify-end absolute bottom-0 left-1/2 -translate-x-1/2`;
      case 'bottom-right':
      default:
        return `${base} items-end justify-end absolute bottom-0 right-0`;
    }
  });

  public dismiss(id: ToastId): void {
    this._store.dismiss(id);
  }

  public async onAction(toast: ToastRecord, event: MouseEvent): Promise<void> {
    if (!toast.action) return;
    await Promise.resolve(toast.action.onClick(event));
    if (!event.defaultPrevented) {
      this.dismiss(toast.id);
    }
  }

  public async onCancel(toast: ToastRecord, event: MouseEvent): Promise<void> {
    if (!toast.cancel) return;
    await Promise.resolve(toast.cancel.onClick(event));
    // Cancel always dismisses (Sonner-like)
    this.dismiss(toast.id);
  }
}
